import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Plus, FileText, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { API } from "@/config";

const PalletLog = ({ workOrder, onPalletAdded }) => {
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // New Pallet Form
  const [newPalletCases, setNewPalletCases] = useState("");
  const [newPalletLot, setNewPalletLot] = useState("");

  // Auto-populate Lot Number when modal opens
  // FIX: This must be placed AFTER the useState definitions above
  useEffect(() => {
    if (showAddModal && workOrder?.planned_date) {
      try {
        const date = new Date(workOrder.planned_date);
        const start = new Date(date.getFullYear(), 0, 0);
        const diff = date - start;
        const oneDay = 1000 * 60 * 60 * 24;
        const dayOfYear = Math.floor(diff / oneDay);
        
        const yy = date.getFullYear().toString().slice(-2);
        const jjj = dayOfYear.toString().padStart(3, '0');
        const line = workOrder.production_line || "L1";
        
        // Only set if currently empty to avoid overwriting user input
        setNewPalletLot(prev => prev || `${line}-${yy}${jjj}`);
      } catch (e) {
        console.error("Error auto-generating lot code", e);
      }
    }
  }, [showAddModal, workOrder]);

  useEffect(() => {
    if (workOrder?.wo_id) {
      fetchPallets();
    }
  }, [workOrder?.wo_id]);

  const fetchPallets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production/${workOrder.wo_id}/pallets`);
      setPallets(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPallet = async () => {
    if (!newPalletCases) return;
    try {
      await axios.post(`${API}/production/${workOrder.wo_id}/pallets`, {
        case_quantity: parseFloat(newPalletCases),
        lot_number: newPalletLot || "LOT-PENDING", // Default if empty
        created_by: "user" 
      });
      toast.success("Pallet Logged Successfully");
      setShowAddModal(false);
      setNewPalletCases("");
      fetchPallets();
      if (onPalletAdded) onPalletAdded(); // Refresh parent WO data
    } catch (e) {
      toast.error("Failed to log pallet");
    }
  };

  const handleViewLabel = async (palletId) => {
    try {
      const res = await axios.get(`${API}/pallets/${palletId}/label`);
      // Create a blob and open in new tab
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, '_blank');
    } catch (e) {
      toast.error("Could not generate label");
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 border-b bg-gray-50">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Package className="w-5 h-5 text-blue-600" />
            Pallet Log
          </CardTitle>
          <Button size="sm" onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1" /> Add Pallet
          </Button>
        </div>
        <div className="text-xs text-gray-500 mt-1 flex justify-between">
            <span>Total Pallets: {pallets.length}</span>
            <span>Total Cases: {pallets.reduce((acc, p) => acc + p.case_quantity, 0)}</span>
        </div>
      </CardHeader>
      
      <CardContent className="p-0 flex-1 overflow-y-auto" style={{ maxHeight: '500px' }}>
        {loading ? (
          <div className="flex justify-center p-6"><Loader2 className="animate-spin text-gray-400" /></div>
        ) : pallets.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            No pallets logged yet.
          </div>
        ) : (
          <div className="divide-y">
            {pallets.map((pallet) => (
              <div key={pallet.pallet_id} className="p-3 hover:bg-gray-50 flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      #{pallet.pallet_sequence}
                    </Badge>
                    <span className="font-mono text-sm font-semibold text-gray-800">
                      {pallet.lp_number}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {format(new Date(pallet.created_at), "MMM d, HH:mm")} • {pallet.case_quantity} Cases
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleViewLabel(pallet.pallet_id)}
                  title="View/Print Label"
                >
                  <FileText className="w-4 h-4 text-gray-600" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Pallet Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log New Pallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Cases on Pallet</Label>
              <Input 
                type="number" 
                value={newPalletCases} 
                onChange={(e) => setNewPalletCases(e.target.value)} 
                placeholder="e.g. 50"
                autoFocus
              />
            </div>
            <div>
              <Label>Lot Number</Label>
              <Input 
                value={newPalletLot} 
                onChange={(e) => setNewPalletLot(e.target.value)} 
                placeholder="Enter Lot Number"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={handleAddPallet} disabled={!newPalletCases}>Log & Generate LP</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PalletLog;